import { ChangeDetectionStrategy, Component, computed, effect, signal, inject } from '@angular/core';
import { BreadcrumbService } from 'xng-breadcrumb';
import { SubscriptionComponent } from '../../../../shared/helpers/subscription.component';
import { OrganizationDetailPageStore } from './organization-detail-page.store';
import { routeParams, translate } from '../../../../shared/shared.global-service';
import { getOrganizationSettingsFrag } from './organization-detail-page.query';
import { OrganizationGlobalStore } from '../../../../../store/organization.global-store';
import { OrganizationSettingsGridObject } from '@libs/types/src/organization/object-type/organization/organization-settings.object';
import { CsvImportSettingsDialogComponent } from '../../../components/organization/csv-import-settings-dialog/csv-import-settings-dialog.component';
import { BaseDialogService } from '../../../../../components/kit/base-dialog/base-dialog-service';
import { BreadcrumbsEnum } from '../../../../shared/models/breadcrumbs.enum';
import { currentValid } from '../../../../shared/pipes/currentValid.pipe';
import { ButtonComponent } from '../../../../../components/kit/button/button.component';
import { Tab, TabList, TabPanel, TabPanels, Tabs } from '@openng/optimus-ui/tabs';
import { TranslatePipe } from '@ngx-translate/core';
import { NgClass } from '@angular/common';
import { Card } from '@openng/optimus-ui/card';
import { OrganizationAdministrationAddressesTileComponent } from '../../../components/organization/organization-administration-addresses-tile/organization-administration-addresses-tile.component';
import { OrganizationStatutaryRepresentativeTableComponent } from '../../../components/organization/organization-statutary-representative-table/organization-statutary-representative-table.component';
import { OrganizationContactPersonsTableComponent } from '../../../components/organization/organization-contact-persons-table/organization-contact-persons-table.component';
import { OrganizationSettingsComponent } from './settings/organization-settings.component';
import { CenterBasicInfosComponent } from '../../../components/organization/center-basic-info/center-basic-infos.component';
import { OrganizationCentersTableComponent } from '../../../components/organization/organization-centers-table/organization-centers-table.component';
import { BackgroundTaskStore } from '../../../../layout/store/background-tasks/background-task.store';
import { BackgroundActionType } from '@libs/types/src/notifications/types/background-action.types';
import { BackgroundTaskLaunchService } from '../../../../layout/store/background-tasks/background-task-launch.service';
import { parseBackgroundTaskParams } from '../../../../layout/store/background-tasks/background-task.utils';
import { FilterBuilder } from '@libs/shared/utils/grid.builder';
import { OrganizationPlatformBillingComponent } from './platform-billing/organization-platform-billing.component';

@Component({
    selector: 'app-organization-detail-page',
    templateUrl: './organization-detail-page.component.html',
    styleUrls: ['./organization-detail-page.component.scss'],
    providers: [OrganizationDetailPageStore],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        ButtonComponent,
        Tabs,
        TabList,
        Tab,
        TranslatePipe,
        NgClass,
        TabPanels,
        TabPanel,
        Card,
        OrganizationAdministrationAddressesTileComponent,
        OrganizationStatutaryRepresentativeTableComponent,
        OrganizationContactPersonsTableComponent,
        OrganizationSettingsComponent,
        CenterBasicInfosComponent,
        OrganizationCentersTableComponent,
        OrganizationPlatformBillingComponent,
    ],
})
export class OrganizationDetailPageComponent extends SubscriptionComponent {
    protected readonly store = inject(OrganizationDetailPageStore);
    protected readonly organizationGlobalStore = inject(
        OrganizationGlobalStore,
    );
    private readonly breadcrumbService = inject(BreadcrumbService);
    private readonly dialogService = inject(BaseDialogService);
    private readonly backgroundTaskStore = inject(BackgroundTaskStore);
    private readonly backgroundTaskLauncher = inject(
        BackgroundTaskLaunchService,
    );

    protected activeTab: string = 'basic';
    protected readonly canViewPlatformBilling = this.store.isAllowedGlobal('organizationBilling.read');
    /** @Deprecated() */
    protected organizationId: number = 0;

    private readonly exportSubmitting = signal(false);
    protected readonly exportPending = this.exportSubmitting.asReadonly();
    private readonly settingsExportSubmitting = signal(false);
    protected readonly settingsExportPending =
        this.settingsExportSubmitting.asReadonly();
    protected readonly exportActive = computed(() => {
        const organizationId = Number(routeParams.organizationId());

        return this.backgroundTaskStore
            .tasks()
            .some(
                (task) =>
                    task.type === BackgroundActionType.ORGANIZATION_EXPORT &&
                    !this.backgroundTaskStore.isTerminal(task.status) &&
                    parseBackgroundTaskParams(task.params)?.organizationId ===
                        organizationId,
            );
    });

    constructor() {
        super();

        effect(() => {
            this.organizationId = Number(routeParams.organizationId());
            const organizationId = routeParams.organizationId();
            if (!organizationId) {
                this.breadcrumbService.set(
                    `@${BreadcrumbsEnum.Organization}`,
                    `${BreadcrumbsEnum.Organization}|${translate.instant('organization.create')}`,
                );
                return;
            }

            this.store.api
                .getOrganization({
                    id: Number(routeParams.organizationId()),
                    centerId: null,
                    validAt: null,
                })
                .then(() => {
                    this.breadcrumbService.set(
                        `@${BreadcrumbsEnum.Organization}`,
                        `${BreadcrumbsEnum.Organization}|${currentValid(this.store.primaryOrganizationCenter()?.basicInfos || [])?.name}`,
                    );
                });
            this.getOrganizationSettings();
        });
        // commented due to take back old process with doOnAction - organizationSettingsStore is the old store
        // this.store.onSuccess(
        //     () => {
        //         routeParams.organizationId() &&
        //         this.store.api.getOrganizationSettings(FilterBuilder.singlePage({ organizationId: routeParams.organizationId() }), getOrganizationSettingsFrag).then();
        //     },
        //     'getOrCreateOrganizationSetting',
        //     'removeOrganizationSetting',
        //     'updateOrganizationSetting',
        // );

        this.store.doOnActions(
            () => {
                routeParams.organizationId() && this.getOrganizationSettings();
            },
            'getOrCreateOrganizationSetting',
            'removeOrganizationSetting',
            'updateOrganizationSetting',
        );

        this.store.doOnActions(
            () => {
                routeParams.organizationId() &&
                    this.store.api
                        .getOrganization({
                            id: Number(routeParams.organizationId()),
                            centerId: null,
                            validAt: null,
                        })
                        .then();
            },
            'linkPerson',
            'unlinkPerson',
            'createPerson',
            'updatePerson',
            'deleteCenter',
            'createBasicInfo',
            'updateBasicInfo',
            'deleteBasicInfo',
            'deleteAddress',
            'updateAddress',
            'createAddress',
            'updateCenter',
        );
    }

    async exportCsv(): Promise<void> {
        if (this.settingsExportPending()) return;
        this.settingsExportSubmitting.set(true);
        try {
            await this.backgroundTaskLauncher.start({
                type: BackgroundActionType.ORGANIZATION_SETTINGS_EXPORT,
                payload: { organizationId: this.organizationId },
                alreadyRunningMessage:
                    'organization-settings.export.messages.alreadyRunning',
                failedMessage:
                    'organization-settings.export.messages.startFailed',
                isSameTask: (task) =>
                    parseBackgroundTaskParams(task.params)?.organizationId ===
                    this.organizationId,
            });
        } finally {
            this.settingsExportSubmitting.set(false);
        }
    }

    async openImportDialog(): Promise<void> {
        const organizationId = this.organizationId;
        const ref = this.dialogService.open(CsvImportSettingsDialogComponent, {
            header: translate.instant('organization-settings.importSettings'),
            styleClass: 'p-dialog-width-60',
            data: { organizationId },
        });
        ref.onClose.subscribe(async (result: boolean) => {
            if (result) {
                this.getOrganizationSettings();
            }
        });
    }

    protected async exportOrganization(): Promise<void> {
        if (this.exportPending()) return;
        this.exportSubmitting.set(true);
        try {
            await this.backgroundTaskLauncher.start({
                type: BackgroundActionType.ORGANIZATION_EXPORT,
                payload: { organizationId: this.organizationId },
                alreadyRunningMessage: 'organization.export.alreadyRunning',
                failedMessage: 'organization.export.failed',
                isSameTask: (task) =>
                    parseBackgroundTaskParams(task.params)?.organizationId ===
                    this.organizationId,
            });
        } finally {
            this.exportSubmitting.set(false);
        }
    }

    private getOrganizationSettings() {
        this.store.api
            .getOrganizationSettings(
                FilterBuilder.singlePage({
                    organizationId: routeParams.organizationId(),
                }),
                getOrganizationSettingsFrag,
            )
            .then((it: OrganizationSettingsGridObject) => {
                this.organizationGlobalStore.settings.set(it?.['items'] || []);
            });
    }
}
